import * as Twig from 'twig';
import moment from 'moment-timezone';
import { currencySymbolMap } from '../../../Core/Users/enums/user-currency';
import { getTextAlign, getTextDirection } from '../../Misc/helpers/formatting/text.helper';
import { bcp47LanguageMap } from '../../../Common/Forms/data/bcp47-language-tags';

import { convertPhpDateFormatToMoment } from './timezone.helper';
/**
 * This file sets up any custom extensions we want to apply to all twig templates
 * This also exists in the backend.
 *
 * See repositiory "chiron" http://git.mediamiser.internal/?p=chiron.git;a=summary
 * File src\server\core\twig\setup.ts
 ***/

/** Creates a regular expression replace filter for the Twig engine */
Twig.extendFilter('regex_replace', (value: string, [regexStr, outputPattern, flags]: [string, string, string?]) => {
    if (value === undefined || value === null) {
        return;
    }

    const regex = new RegExp(regexStr, flags);

    return value.replace(regex, outputPattern);
});

// A few commonly re-used regexes so we don't have to keep re-creating them
const specialCharactersPattern = /[\-\[\]\/\{\}\(\)\+\.\\\^\$\|]/g;
const starCharactersPattern = /\*/g;
const questionMarkCharactersPattern = /\?/g;
const apostropheMarkCharactersPattern = /['’]/g;

/** Creates a highlight keywords pipe for the twig engine */
Twig.extendFilter(
    'highlight',
    (
        value: string,
        [keywords, styles, caseSensitive]: [string[], string, boolean]
    ) => {
        if (value === undefined || value === null) {
            return '';
        }
        if (!keywords || keywords.length < 1) {
            return value;
        }
        if (!styles) {
            styles = 'background-color: #ffec87; color: #292929';
        }
        caseSensitive = !!caseSensitive;

        const searchTermsMap = keywords.map(searchTerm => {
            searchTerm = searchTerm.replace(specialCharactersPattern, '\\$&');

            // Replace wildcard character "*" with regex that will match the rest of the word.
            searchTerm = searchTerm.replace(starCharactersPattern, '\\w*');

            // Replace wildcard character "?" with regex that will match 1 character.
            searchTerm = searchTerm.replace(questionMarkCharactersPattern, '.');

            // Replace either apostrophe characters ' or ’ with regex that will match either of them.
            searchTerm = searchTerm.replace(apostropheMarkCharactersPattern, '[\'’]');

            return searchTerm;
        });

        const expFlag = caseSensitive ? 'g' : 'gi';
        const searchTerms = searchTermsMap.join('|');
        const regex = new RegExp('<|>|\\b(' + searchTerms + ')\\b', expFlag);

        let level = 0;

        return value.replace(
            regex,
            (match) => {
                // Don't match inside of html tags
                if (match === '<') {
                    level++;

                    return match;
                } else if (match === '>') {
                    level--;

                    return match;
                }

                if (level > 0) {
                    return match;
                }

                return `<span style="${styles}">${match}</span>`;
            }
        );
    }
);

/**
 * Acts as a replacement to the date() function, which does not allow the user to pass their own timezone identifier
 **/
Twig.extendFilter(
    'date_with_timezone',
    (value: string, [formatString, timezoneStr]: [string, string]) => {
        if (!timezoneStr) {
            timezoneStr = 'UTC';
        }
        const fmt = convertPhpDateFormatToMoment(formatString);
        if (value === undefined || value === null) {
            return moment().locale('en').tz(timezoneStr).format(fmt);
        }

        // Manually set 'en' for backwards compatibility
        return moment(+value).locale('en').tz(timezoneStr).format(fmt);
    }
);

Twig.extendFilter(
    'localized_date',
    (value: string, [formatString, locale]: [string, string]) => {
        const fmt = convertPhpDateFormatToMoment(formatString);
        if (value === undefined || value === null) {
            return moment().locale(locale).format(fmt);
        } else {
            return moment(+value).locale(locale).format(fmt);
        }
    }
);

Twig.extendFilter(
    'localized_date_with_timezone',
    (value: string, [formatString, locale, timezoneStr]: [string, string, string]) => {
        if (!timezoneStr) {
            timezoneStr = 'UTC';
        }
        const fmt = convertPhpDateFormatToMoment(formatString);
        if (value === undefined || value === null) {
            return moment().locale(locale).tz(timezoneStr).format(fmt);
        }

        return moment(+value).locale(locale).tz(timezoneStr).format(fmt);
    }
);

/**
 * return the text direction
 **/
Twig.extendFilter(
    'get_text_direction',
    (value: string) => {
        return getTextDirection(value);
    }
);

/**
 * return the text align
 **/
Twig.extendFilter(
    'get_text_align',
    (value: string) => {
        return getTextAlign(value);
    }
);

interface MentionUrlValue {
    publishedDate: Date;
    url?: string;
    criticalMentionWordPlayUrl?: string;
    viewerUrl?: string;
    enforceConcisePlayer?: boolean;
    cachedUrl?: string;
    tvEyesInstaPlayUrl?: string;
};
// Dynamically constructs the external URL for a mention in a Twig template
Twig.extendFilter(
    'mention_url',
    (value: MentionUrlValue) => {
        const mention: MentionUrlValue = value;
        /// TODO: Update these URLs to be an environment variable
        const hasUrl: boolean = typeof mention.url === 'string';
        const isViewUrl: boolean = !!mention.viewerUrl;

        const criticalMentionDomains: string[] = ['https://api.criticalmention.com/', 'https://staging-partner.criticalmention.com'];
        const isCriticalMentionURL: boolean = hasUrl && !!criticalMentionDomains.find(criticalMentionUrl => mention.url.includes(criticalMentionUrl));
        const isWordplayURL: boolean = !!mention.criticalMentionWordPlayUrl;
        const isCriticalMention: boolean = isWordplayURL || isCriticalMentionURL;

        const tvEyesDomains = ['http://mms.tveyes.com/', 'https://mms.tveyes.com/', 'http://archive.tveyes.com/'];
        const tvEyesArchiveDomain = 'http://archive.tveyes.com/';
        const isTVEyesMentionURL = hasUrl && !!tvEyesDomains.find(domain => mention.url.startsWith(domain));
        const hasTVEyesInstaPlayUrl = !!mention.tvEyesInstaPlayUrl;
        const isTVEyesArchiveMention = isTVEyesMentionURL && mention.url.startsWith(tvEyesArchiveDomain);

        const nlaEclipsUrl: string = 'https://www.nla-eclips.com';
        const isNlaEclips: boolean = hasUrl && mention.url.startsWith(nlaEclipsUrl);

        let urlParameters: string = '';
        let mentionUrl: string = null;

        if (mention.cachedUrl) {
            return mention.cachedUrl;
        }


        if (isCriticalMention) {
            /// TODO: Update this URL to be an environment variable
            urlParameters = '&ess=https://agility.agilitypr.com/app/assets/css/cm-min.css';
        }

        if (isWordplayURL) {
            mentionUrl = mention.criticalMentionWordPlayUrl;
        } else if (isViewUrl && isTVEyesArchiveMention && mention.enforceConcisePlayer) {
            mentionUrl = mention.url;
        } else if (isViewUrl) {
            mentionUrl = mention.viewerUrl;
        } else {
            mentionUrl = mention.url;
        }

        if (isTVEyesMentionURL && !isTVEyesArchiveMention && hasTVEyesInstaPlayUrl && !mention.enforceConcisePlayer) {
            mentionUrl = mention.tvEyesInstaPlayUrl;
        }

        if (mentionUrl) {
            mentionUrl = mentionUrl + urlParameters;
        }

        // NLA should never use mentionViewer url
        if (isNlaEclips) {
            mentionUrl = mention.url;
        }

        return mentionUrl;
    }
);

Twig.extendFilter(
    'currency_conversion',
    (value: number, [targetCurrency, rate]: [string, number]) => {

        if (value === undefined || value === null) {
            return '';
        }

        if (!rate){
            rate = 1;
        }

        if (!targetCurrency){
            targetCurrency = 'USD';
        }
        const converted = value * rate;

        const symbol = currencySymbolMap.get( targetCurrency );
        if (symbol){
            return symbol + converted.toLocaleString( undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
        } else {
            return converted.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 }) + ' ' + targetCurrency;
        }
    }
);

/** Outlook does not respect break-word, and here we create a pipe to use break all for long strings */
Twig.extendFilter(
    'break_long_string',
    (value: string) => {
        if (!value) {
            return value;
        }

        const strings = value.split(' ');
        let res = '';

        strings.forEach(str => {
            if (str.length >= 80) {
                str = '<p style="word-break:break-all; margin: 0; display: initial;"> ' + str + ' </p>';
            }

            res += str + ' ';
        });

        return res;
    }
);

/**
 * Returns the string representation of the language bcp47 code (which includes iso)
 **/
Twig.extendFilter(
    'get_language_from_iso',
    (value: string) => {
        if (value === undefined || value === null) {
            return bcp47LanguageMap['en'];
        }

        if (bcp47LanguageMap[value]) {
            return bcp47LanguageMap[value];
        }

        return bcp47LanguageMap['en'];
    }
);

/**
 * B/E sometimes provide base url ends with '/', sometimes not. This generates problem to outlook and outlook web
 * Created check_base_url to check base url and make sure it ends with '/'
 */
Twig.extendFilter(
    'check_base_url',
    (value: string) => {

        let res = value;
        if (!res) {
            res = window.location.origin;
        }

        const lastChar = res.slice(-1);

        if (lastChar !== '/') {
            res += '/';
        }

        return res;
    }
);

/**
 *
 * This function handles replacing the base application url (i.e. https://app.agilitypr.com) with the whitelabel equivalent
 * (https://burrelles.agilitypr.com).  The value is pulled from src/server/templates/BAL/config/transactional-email.config.ts
 * and values specific to the environment.
 */
Twig.extendFilter(
    'whitelabel_url',
    (value: string, [data]: [any]): string => {
        let returnUrl = value;

        if (data.whitelabelUrl) {
            if (data.neptuneApplicationUrl) {
                returnUrl = returnUrl.replace(data.neptuneApplicationUrl, data.whitelabelUrl);
            }
            if (data.agilityApplicationUrl) {
                returnUrl = returnUrl.replace(data.agilityApplicationUrl, data.whitelabelUrl);
            }
        }

        return returnUrl;
    }
);

export default Twig;
